import { useCallback, useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

/**
 * Number of open tasks assigned to the employee that they haven't opened yet
 * (not in the task's `viewedBy`). Re-reads every `pollMs` when given; call
 * `refresh` after the user has looked at their tasks.
 */
const useUnseenTaskCount = (employeeId, pollMs) => {
  const [unseenCount, setUnseenCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!employeeId) return;
    try {
      const snapshot = await getDocs(collection(db, 'Tasks'));
      const count = snapshot.docs.reduce((acc, d) => {
        const task = d.data();
        const isMine =
          task.assigneeId === employeeId ||
          (task.assignees && task.assignees.some(a => a.id === employeeId));
        if (!isMine) return acc;
        if (task.status === 'completed' || task.status === 'cancelled') return acc;
        const seen = Array.isArray(task.viewedBy) && task.viewedBy.includes(employeeId);
        return seen ? acc : acc + 1;
      }, 0);
      setUnseenCount(count);
    } catch (err) {
      console.error('Error fetching unseen tasks:', err);
    }
  }, [employeeId]);

  useEffect(() => {
    refresh();
    if (!pollMs) return undefined;
    const interval = setInterval(refresh, pollMs);
    return () => clearInterval(interval);
  }, [refresh, pollMs]);

  return { unseenCount, refresh };
};

export default useUnseenTaskCount;
